import { Crosstab } from '@hotjar/webapp/utils/crosstab';
import { SessionStorage } from '@hotjar/insights-common/lib/storage-factory';

type Container = {
  [key: string]: unknown;
};

type Callback = (...args: unknown[]) => void;

/**
 * A session storage that works across tabs. It's cleared out when all tabs are closed
 */
export class SharedSessionStorageCtor {
  private containerKey = 'hotjar.sharedstorage';

  private watchers: Array<[string, Callback]> = [];

  constructor() {
    // listen to "data changed" or "data requested" from other tabs
    Crosstab.listen(
      'sharedStorageModified',
      (value) => {
        const container = this.handleNotification(value as string);
        if (!container) {
          return;
        }

        this.notifyWatchers(container);
      },
      true,
    );

    Crosstab.listen(
      'broadcastSharedStorageRequest',
      // answer other tab request to broadcast shared storage
      () => this.notify(),
      true,
    );

    // get initial value by sending request to other tab
    Crosstab.signal('broadcastSharedStorageRequest', undefined, true);
  }

  private notifyWatchers(container: Container, onlySpecificCallback?: Callback) {
    this.watchers.forEach((keyAndCallback: [string, Callback]) => {
      const key = keyAndCallback[0];
      const callback = keyAndCallback[1];
      if (onlySpecificCallback && callback !== onlySpecificCallback) return;

      if (key in container) {
        try {
          callback(container[key]);
        } catch (err) {
          // pass
        }
      }
    });
  }

  // eslint-disable-next-line class-methods-use-this
  private notifyOtherTabs = (container: Container) => {
    Crosstab.signal('sharedStorageModified', JSON.stringify(container), true);
  };

  /**
   * Notify other tabs that data is changed.
   */
  private notify() {
    const container = SessionStorage.get(this.containerKey) as Container;
    if (!container) return;

    this.notifyWatchers(container);
    this.notifyOtherTabs(container);
  }

  /**
   * Handle "data changed" notifications from other tabs.
   */
  private handleNotification(value: string): Container | null {
    if (!value) return null;

    const container = JSON.parse(value);
    SessionStorage.set(this.containerKey, container);

    return container;
  }

  /**
   * Get a value.
   *
   * Deserialize the JSON object back to its original type.
   *
   * @param key Key to get.
   * @param defaultValue [optional] Default value to return in case the key is not found.
   * @returns {*}
   */
  public get(key: string, defaultValue?: unknown): unknown {
    const container = (SessionStorage.get(this.containerKey) || {}) as Container;

    if (key in container) return container[key];
    return defaultValue;
  }

  /**
   * Set a value.
   *
   * Serialize the object to JSON so we can store any kind of value.
   *
   * @param key Key to set.
   * @param value Value to set.
   */
  public set(key: string, value: unknown): void {
    if (key === '__proto__' || key === 'constructor') return;

    const container = (SessionStorage.get(this.containerKey) || {}) as Container;
    container[key] = value;

    SessionStorage.set(this.containerKey, container);
    this.notify();
  }

  /**
   * Remove an item.
   *
   * @param key Key to remove.
   */
  public remove(key: string): void {
    const container = (SessionStorage.get(this.containerKey) || {}) as Container;
    if (!(key in container)) return;

    delete container[key];
    SessionStorage.set(this.containerKey, container);

    this.notify();
  }

  /**
   * Register callback for value of a key changed - callback can be invoked multiple times with the same value.
   * */
  public onChange(key: string, callback: Callback): void {
    this.watchers.push([key, callback]);

    const container = SessionStorage.get(this.containerKey) as Container;
    if (!container) return;

    this.notifyWatchers(container, callback);
  }
}

export const SharedSessionStorage = new SharedSessionStorageCtor();
